/**
 * Reports Hooks
 *
 * Provides hooks for report data fetching with TanStack Query.
 */

import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from './queryKeys';
import type {
  CustomerSatisfactionData,
  SatisfactionTrend,
  AgentPerformanceData,
  AgentPerformanceTrend,
} from '@/types';

// API response types

export interface RevenueDataPoint {
  date: string;
  revenue: number;
  labor: number;
  parts: number;
  workOrders: number;
}

export interface ReportSummary {
  totalRevenue: number;
  revenueChange: number;
  workOrdersCompleted: number;
  workOrdersChange: number;
  avgTicketValue: number;
  avgTicketChange: number;
  laborHours: number;
  laborHoursChange: number;
}

export interface MechanicPerformanceItem {
  mechanicId: string;
  name: string;
  workOrders: number;
  revenue: number;
  laborHours: number;
  efficiency: number;
}

export interface ServiceTypeStatItem {
  name: string;
  count: number;
  revenue: number;
  avgDuration: number;
}

export interface MonthlyComparisonItem {
  month: string;
  current: number;
  previous: number;
}

export interface CustomerStats {
  newCustomers: number;
  returningCustomers: number;
  totalCustomers: number;
  avgVisitsPerCustomer: number;
  topCustomers: { name: string; revenue: number; visits: number }[];
}

// Hooks

export function useRevenueReport(dateFrom: string, dateTo: string, groupBy: string = 'day') {
  return useQuery({
    queryKey: queryKeys.reports.revenue({ dateFrom, dateTo, groupBy }),
    queryFn: () =>
      api.get<{ data: RevenueDataPoint[] }>('/reports/revenue', {
        params: { dateFrom, dateTo, groupBy },
      }),
    enabled: !!dateFrom && !!dateTo,
    staleTime: 60 * 1000,
  });
}

export function useReportSummary(dateFrom: string, dateTo: string) {
  return useQuery({
    queryKey: queryKeys.reports.summary({ dateFrom, dateTo }),
    queryFn: () =>
      api.get<ReportSummary>('/reports/summary', {
        params: { dateFrom, dateTo },
      }),
    enabled: !!dateFrom && !!dateTo,
    staleTime: 60 * 1000,
  });
}

export function useMechanicPerformance(dateFrom: string, dateTo: string) {
  return useQuery({
    queryKey: queryKeys.reports.mechanicPerformance({ dateFrom, dateTo }),
    queryFn: () =>
      api.get<{ data: MechanicPerformanceItem[] }>('/reports/mechanic-performance', {
        params: { dateFrom, dateTo },
      }),
    enabled: !!dateFrom && !!dateTo,
    staleTime: 60 * 1000,
  });
}

export function useServiceTypeStats(dateFrom: string, dateTo: string) {
  return useQuery({
    queryKey: queryKeys.reports.serviceTypes({ dateFrom, dateTo }),
    queryFn: () =>
      api.get<{ data: ServiceTypeStatItem[] }>('/reports/service-types', {
        params: { dateFrom, dateTo },
      }),
    enabled: !!dateFrom && !!dateTo,
    staleTime: 60 * 1000,
  });
}

export function useMonthlyComparison(year?: number) {
  return useQuery({
    queryKey: queryKeys.reports.monthlyComparison(year),
    queryFn: () =>
      api.get<{ data: MonthlyComparisonItem[] }>('/reports/monthly-comparison', {
        params: year ? { year } : {},
      }),
    staleTime: 5 * 60 * 1000,
  });
}

export function useCustomerStats(dateFrom: string, dateTo: string) {
  return useQuery({
    queryKey: queryKeys.reports.customerStats({ dateFrom, dateTo }),
    queryFn: () =>
      api.get<CustomerStats>('/reports/customer-stats', {
        params: { dateFrom, dateTo },
      }),
    enabled: !!dateFrom && !!dateTo,
    staleTime: 60 * 1000,
  });
}

export interface CustomerSatisfactionReport extends CustomerSatisfactionData {
  trend: SatisfactionTrend[];
}

export function useCustomerSatisfaction(dateFrom: string, dateTo: string, groupBy = 'day') {
  return useQuery({
    queryKey: queryKeys.reports.customerSatisfaction({ dateFrom, dateTo, groupBy }),
    queryFn: () =>
      api.get<CustomerSatisfactionReport>('/reports/customer-satisfaction', {
        params: { dateFrom, dateTo, groupBy },
      }),
    enabled: !!dateFrom && !!dateTo,
    staleTime: 60 * 1000,
  });
}

export interface AgentPerformanceReport extends AgentPerformanceData {
  trend: AgentPerformanceTrend[];
}

export function useAgentPerformance(dateFrom: string, dateTo: string, groupBy = 'day') {
  return useQuery({
    queryKey: queryKeys.reports.agentPerformance({ dateFrom, dateTo, groupBy }),
    queryFn: () =>
      api.get<AgentPerformanceReport>('/reports/agent-performance', {
        params: { dateFrom, dateTo, groupBy },
      }),
    enabled: !!dateFrom && !!dateTo,
    staleTime: 60 * 1000,
  });
}

/** Decimal hours and ratios from GET /reports/work-order-duration-performance (#2707). */
export interface DurationPerformanceMetrics {
  plannedHours: number;
  billableHours: number | null;
  actualHours: number;
  /** billable / actual; null when actual is 0 or billable is null (fixed-price). */
  efficiency: number | null;
  /** planned / actual; null when actual is 0. */
  planningAccuracy: number | null;
}

export interface MechanicDurationPerformanceRow extends DurationPerformanceMetrics {
  mechanicId: string | null;
  name: string | null;
}

export interface ServiceDurationPerformanceRow extends DurationPerformanceMetrics {
  serviceId: string | null;
  name: string | null;
}

export interface WorkOrderDurationPerformanceReport {
  mechanics: MechanicDurationPerformanceRow[];
  services: ServiceDurationPerformanceRow[];
}

export function useWorkOrderDurationPerformance(dateFrom: string, dateTo: string) {
  return useQuery({
    queryKey: queryKeys.reports.workOrderDurationPerformance({ dateFrom, dateTo }),
    queryFn: () =>
      api.get<WorkOrderDurationPerformanceReport>('/reports/work-order-duration-performance', {
        params: { dateFrom, dateTo },
      }),
    enabled: !!dateFrom && !!dateTo,
    staleTime: 60 * 1000,
  });
}
